<template>
  <Teleport to="body">
    <transition name="sheet-fade">
      <div v-if="visible" class="message-info-overlay" @click.self="close">
        <transition name="sheet-slide">
          <div v-if="visible" class="message-info-sheet">
            <!-- Header -->
            <div class="sheet-header">
              <div class="sheet-handle"></div>
              <div class="sheet-title">
                <i class="bi bi-info-circle"></i>
                <span>{{ $t('chat.message_info') || 'Message Info' }}</span>
              </div>
              <button class="sheet-close" @click="close">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            
            <!-- Loading State -->
            <div v-if="loading" class="sheet-loading">
              <div class="spinner"></div>
              <span>{{ $t('common.loading') || 'Loading...' }}</span>
            </div>
            
            <!-- Content -->
            <div v-else class="sheet-content">
              <!-- Message Preview -->
              <div class="info-section message-preview">
                <div class="preview-icon">
                  <i :class="messageIcon"></i>
                </div>
                <div class="preview-text">
                  {{ messagePreviewText }}
                </div>
              </div>
              
              <!-- Delivery Info -->
              <div class="info-section">
                <div class="section-header">
                  <i class="bi bi-check-circle"></i>
                  <span>{{ $t('chat.delivery_info') || 'Delivery Info' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('chat.sent_at') || 'Sent' }}</span>
                  <span class="info-value">{{ formatFullTimestamp(freshMessage.createdAt) }}</span>
                </div>
                <div v-if="freshMessage.editedAt" class="info-row">
                  <span class="info-label">{{ $t('chat.edited_at') || 'Edited' }}</span>
                  <span class="info-value">{{ formatFullTimestamp(freshMessage.editedAt) }}</span>
                </div>
              </div>
              
              <!-- Read Receipts -->
              <div v-if="readByUsers.length > 0" class="info-section">
                <div class="section-header">
                  <i class="bi bi-check2-all"></i>
                  <span>{{ $t('chat.read_by') || 'Read by' }} ({{ readByUsers.length }})</span>
                </div>
                <div 
                  v-for="user in readByUsers" 
                  :key="user.id" 
                  class="read-by-item"
                >
                  <div class="read-by-avatar">
                    <img v-if="user.avatar" :src="user.avatar" @error="onAvatarError" alt="" />
                    <div v-else class="avatar-placeholder">{{ user.initial }}</div>
                  </div>
                  <span class="read-by-name">{{ user.name }}</span>
                  <i class="bi bi-check2-all read-by-check"></i>
                </div>
              </div>
              
              <!-- Not Read Yet (for group chats) -->
              <div v-if="notReadByUsers.length > 0" class="info-section">
                <div class="section-header">
                  <i class="bi bi-check2"></i>
                  <span>{{ $t('chat.delivered_to') || 'Delivered to' }} ({{ notReadByUsers.length }})</span>
                </div>
                <div 
                  v-for="user in notReadByUsers" 
                  :key="user.id" 
                  class="read-by-item not-read"
                >
                  <div class="read-by-avatar">
                    <img v-if="user.avatar" :src="user.avatar" @error="onAvatarError" alt="" />
                    <div v-else class="avatar-placeholder">{{ user.initial }}</div>
                  </div>
                  <span class="read-by-name">{{ user.name }}</span>
                  <i class="bi bi-check2 read-by-check"></i>
                </div>
              </div>
              
              <!-- Reactions -->
              <div v-if="hasReactions" class="info-section">
                <div class="section-header">
                  <i class="bi bi-emoji-smile"></i>
                  <span>{{ $t('chat.reactions') || 'Reactions' }}</span>
                </div>
                <div class="reactions-grid">
                  <div 
                    v-for="(data, emoji) in freshMessage.reactions" 
                    :key="emoji"
                    class="reaction-item"
                  >
                    <span class="reaction-emoji">{{ emoji }}</span>
                    <span class="reaction-count">{{ data.count || 1 }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { ChatService } from '@/services/chat';
import { chatState } from '@/stores/chat';

export default {
  name: 'MessageInfoSheet',
  
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    message: {
      type: Object,
      required: true,
    },
    conversationId: {
      type: String,
      required: true,
    },
    participants: {
      type: Object,
      default: () => ({}),
    },
  },
  
  emits: ['close'],
  
  data() {
    return {
      loading: true,
      freshMessage: null,
    };
  },
  
  computed: {
    messageIcon() {
      if (this.hasAttachment) {
        const subtype = this.freshMessage?.custom?.message_type || 
                       this.freshMessage?.content?.[0]?.subtype;
        if (subtype === 'voice' || subtype === 'audio') return 'bi bi-mic-fill';
        if (subtype === 'video') return 'bi bi-camera-video-fill';
        if (subtype === 'image') return 'bi bi-image-fill';
        return 'bi bi-paperclip';
      }
      return 'bi bi-chat-text-fill';
    },
    
    messagePreviewText() {
      const msg = this.freshMessage;
      if (!msg) return '';
      
      if (msg.text && msg.text.trim()) {
        return msg.text.length > 100 ? msg.text.substring(0, 100) + '...' : msg.text;
      }
      
      if (this.hasAttachment) {
        const subtype = msg.custom?.message_type || msg.content?.[0]?.subtype;
        if (subtype === 'voice' || subtype === 'audio') {
          return this.$t('chat.msg_voice') || 'Voice message';
        }
        if (subtype === 'video') {
          return this.$t('chat.msg_video') || 'Video';
        }
        if (subtype === 'image') {
          return this.$t('chat.msg_image') || 'Image';
        }
        return this.$t('chat.attachment') || 'Attachment';
      }
      
      return this.$t('chat.no_content') || 'No content';
    },
    
    hasAttachment() {
      const msg = this.freshMessage;
      return msg && (msg.attachment || (msg.content && msg.content.length > 0));
    },
    
    hasReactions() {
      return this.freshMessage?.reactions && 
             Object.keys(this.freshMessage.reactions).length > 0;
    },
    
    readByUsers() {
      const msg = this.freshMessage;
      if (!msg || !msg.readBy || msg.readBy.length === 0) return [];
      
      return msg.readBy
        .filter(userId => String(userId) !== String(chatState.userId))
        .map(userId => this.getUserInfo(userId));
    },
    
    notReadByUsers() {
      const msg = this.freshMessage;
      if (!msg) return [];
      
      // Get all participants except sender and those who read
      const readBySet = new Set((msg.readBy || []).map(String));
      readBySet.add(String(msg.senderId)); // Exclude sender
      
      const participantIds = Object.keys(this.participants || {});
      return participantIds
        .filter(id => !readBySet.has(String(id)))
        .map(id => this.getUserInfo(id));
    },
  },
  
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadMessageInfo();
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }
      },
    },
  },
  
  methods: {
    async loadMessageInfo() {
      this.loading = true;
      this.freshMessage = this.message;
      
      try {
        // Fetch fresh message data from API
        const fresh = await ChatService.getMessage({
          conversationId: this.conversationId,
          messageId: this.message.id,
        });
        
        if (fresh) {
          this.freshMessage = fresh;
        }
      } catch (e) {
        console.error('[MessageInfoSheet] Failed to load message:', e);
      } finally {
        this.loading = false;
      }
    },
    
    getUserInfo(userId) {
      const participant = this.participants[String(userId)];
      
      if (participant) {
        return {
          id: userId,
          name: participant.name || participant.username || `User ${userId}`,
          avatar: participant.avatar || participant.photoUrl,
          initial: (participant.name || participant.username || 'U').charAt(0).toUpperCase(),
        };
      }
      
      // Fallback
      return {
        id: userId,
        name: `User ${userId}`,
        avatar: null,
        initial: 'U',
      };
    },
    
    formatFullTimestamp(timestamp) {
      if (!timestamp) return '';
      
      const date = typeof timestamp === 'number'
        ? new Date(timestamp)
        : new Date(timestamp);
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const diffDays = Math.floor((today - msgDate) / (1000 * 60 * 60 * 24));
      
      const timeStr = date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      
      if (diffDays === 0) {
        return `${this.$t('chat.today') || 'Today'} ${timeStr}`;
      } else if (diffDays === 1) {
        return `${this.$t('chat.yesterday') || 'Yesterday'} ${timeStr}`;
      } else {
        const dateStr = date.toLocaleDateString([], {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        return `${dateStr} ${timeStr}`;
      }
    },
    
    onAvatarError(e) {
      e.target.style.display = 'none';
    },
    
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
/* Overlay */
.message-info-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

/* Sheet */
.message-info-sheet {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 24px 24px 0 0;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 
    0 -10px 40px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  animation: sheetSlideUp 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes sheetSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.sheet-header {
  padding: 16px 20px 20px;
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%);
  position: relative;
  border-bottom: none;
}

.sheet-handle {
  width: 48px;
  height: 5px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 3px;
  margin: 0 auto 14px;
}

.sheet-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.sheet-title i {
  color: #ffffff;
  font-size: 20px;
}

.sheet-close {
  position: absolute;
  top: 18px;
  right: 16px;
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  transition: all 0.25s ease;
  backdrop-filter: blur(4px);
}

.sheet-close:hover {
  background: rgba(255, 255, 255, 0.35);
  transform: rotate(90deg);
}

/* Loading */
.sheet-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--bs-secondary-color, #6c757d);
}

.sheet-loading .spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--bs-border-color, #e5e5e5);
  border-top-color: var(--bs-success, #198754);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Content */
.sheet-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8fafc;
}

/* Info Section */
.info-section {
  margin-bottom: 24px;
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.info-section:last-child {
  margin-bottom: 0;
}

.info-section.message-preview {
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #00c444;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.section-header i {
  font-size: 16px;
}

/* Message Preview */
.message-preview {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%);
  border-radius: 16px;
  box-shadow: 0 4px 14px rgba(0, 196, 68, 0.25);
}

.preview-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  backdrop-filter: blur(4px);
}

.preview-icon i {
  color: #ffffff !important;
}

.preview-text {
  flex: 1;
  font-size: 14px;
  color: #ffffff;
  line-height: 1.5;
  word-break: break-word;
  font-weight: 500;
}

/* Info Row */
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.info-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.info-value {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

/* Read By Item */
.read-by-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}

.read-by-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.read-by-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bs-success, #198754);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
}

.read-by-name {
  flex: 1;
  font-size: 14px;
  color: var(--bs-body-color, #333);
}

.read-by-check {
  color: var(--bs-success, #198754);
  font-size: 16px;
}

.read-by-item.not-read .read-by-check {
  color: var(--bs-secondary-color, #6c757d);
}

/* Reactions Grid */
.reactions-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.reaction-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: var(--bs-success-bg-subtle, #d1e7dd);
  border: 1px solid var(--bs-success-border-subtle, #a3cfbb);
  border-radius: 20px;
}

.reaction-emoji {
  font-size: 18px;
}

.reaction-count {
  font-size: 14px;
  font-weight: 600;
  color: var(--bs-body-color, #333);
}

/* Transitions */
.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.3s ease;
}

.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;
}

.sheet-slide-enter-active,
.sheet-slide-leave-active {
  transition: transform 0.3s ease;
}

.sheet-slide-enter-from,
.sheet-slide-leave-to {
  transform: translateY(100%);
}

/* Dark mode */
[data-bs-theme="dark"] .message-info-sheet {
  background: linear-gradient(145deg, #1e1e32 0%, #16162a 100%);
}

[data-bs-theme="dark"] .sheet-content {
  background: #12122a;
}

[data-bs-theme="dark"] .info-section {
  background: #1a1a2e;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .info-section.message-preview {
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%);
}

[data-bs-theme="dark"] .section-header {
  border-color: #2d2d44;
}

[data-bs-theme="dark"] .info-row {
  border-color: #2d2d44;
}

[data-bs-theme="dark"] .info-label {
  color: #e5e5e5;
}

[data-bs-theme="dark"] .info-value {
  color: #9ca3af;
}

[data-bs-theme="dark"] .read-by-name {
  color: #e5e5e5;
}

[data-bs-theme="dark"] .reaction-item {
  background: #2d2d48;
  border-color: #3d3d5c;
}

[data-bs-theme="dark"] .reaction-count {
  color: #e5e5e5;
}

/* Responsive */
@media (min-width: 768px) {
  .message-info-overlay {
    align-items: center;
  }
  
  .message-info-sheet {
    border-radius: 20px;
    max-height: 70vh;
  }
}
</style>
